import React from 'react';
import gql from 'graphql-tag';
import PvsVerticalListContainer from '../../../containers/PvsVerticalListContainer';
import PvsCampaignContainer, {
  GET_PVS_CAMPAIGN_QUERY,
  GET_CLIENT_PVS_CAMPAIGN_MODULE
} from '../../../containers/PvsCampaignContainer';
import PvsCampaignEdit from './PvsCampaignEdit';
import { CampaignModuleProps, PvsCampaign } from 'models/interfaces';
import { PureQueryOptions, ApolloClient } from 'apollo-client';
import { GET_CAMPAIGN_QUERY } from 'containers/ThomasCampaignContainer';
import PvsCampaignPublish from './PvsCampaignPublish';
import PvsCreativeContainer from 'containers/PvsCreativeContainer';
import { Query } from 'react-apollo';

const SAVE_PVS_CAMPAIGN_MUTATION = gql`
  mutation SaveCampaign($campaign: PvsCampaignInput) {
    savePvsCampaign(campaign: $campaign) {
      pvs_campaign_id
      tgrams_id
      ad_server_campaign_id
      vertical_id
      weight
      ad_server_name
      start_date
      end_date
      is_active
      last_sync_date
      is_dirty
      last_update_date
    }
  }
`;

const PUBLISH_PVS_CAMPAIGN_MUTATION = gql`
  mutation PublishCampaign($pvsInput: PvsPublishInput) {
    pvsPublish(pvsInput: $pvsInput)
  }
`;

/**
 * Will return queries to be performed after a gql mutation that are intended
 * to refresh the Apollo cache.
 * @param campaignId PvsCampaign UUID to execute queries with
 */
function refetchQueries(campaignId: string) {
  const refetchPvs = {
    query: GET_PVS_CAMPAIGN_QUERY,
    variables: { pvs_campaign_id: campaignId }
  } as PureQueryOptions;

  const refetchThomasCampaign = {
    query: GET_CAMPAIGN_QUERY,
    variables: { campaign_id: campaignId }
  } as PureQueryOptions;

  return [refetchThomasCampaign, refetchPvs];
}

/**
 * Will execute a gql query to retrieve a PvsCampaign for the given campaignId
 * @param apolloClient ApolloClient<any> uses to execute a mutation
 * @param campaignId PvsCampaign UUID
 */
async function getCampaign(apolloClient: ApolloClient<any>) {
  let campaignModule: PvsCampaign;

  try {
    const data: any = await apolloClient.cache.readQuery({
      query: GET_CLIENT_PVS_CAMPAIGN_MODULE
    });

    campaignModule = data.campaignModule;
  } catch (error) {
    console.log('Failed to retrieve campaignModule from cache.');
    console.error(error);
    throw error(error);
  }

  return campaignModule;
}

/**
 * Will execute a gql mutation to duplicate the given PvsCampaign
 * @param apolloClient ApolloClient<any> uses to execute a mutation
 * @param campaign PvsCampaign object to be duplicated
 * @param newCampaignId a new UUID that will be assigned to the duplicated campaign
 */
async function duplicateCampaign(
  apolloClient: ApolloClient<any>,
  campaign: PvsCampaign,
  newCampaignId: string
) {
  console.log('pvsCampaign postModel: ', campaign);

  // create pvs campaign db data
  const duplicateCampaign = {
    pvs_campaign_id: newCampaignId,
    tgrams_id: campaign.tgrams_id,
    advertiser: campaign.advertiser,
    ad_server_campaign_id: null,
    vertical_id: campaign.vertical_id,
    vertical: campaign.vertical,
    weight: campaign.weight,
    ad_server_name: campaign.ad_server_name,
    start_date: '',
    end_date: '',
    is_active: true,
    last_sync_date: null,
    is_dirty: true
  };

  const pvsSaveResponse = await apolloClient.mutate({
    mutation: SAVE_PVS_CAMPAIGN_MUTATION,
    variables: { campaign: duplicateCampaign },
    refetchQueries: refetchQueries(campaign.pvs_campaign_id)
  });

  console.log('pvsSaveResponse: ', pvsSaveResponse);
}

/**
 * Will execute a gql mutation to save the given PvsCampaign
 * @param apolloClient ApolloClient<any> uses to execute a mutation
 * @param campaign PvsCampaign object to be saved
 */
async function saveCampaign(apolloClient: ApolloClient<any>, campaign: PvsCampaign) {
  let campaignModule: PvsCampaign;

  try {
    const data: any = await apolloClient.cache.readQuery({
      query: GET_CLIENT_PVS_CAMPAIGN_MODULE
    });

    campaignModule = data.campaignModule;
  } catch (error) {
    console.log('Failed to retrieve campaignModule from cache.');
    console.error(error);
    throw error(error);
  }

  try {
    console.log('pvsCampaign postModel: ', campaignModule);

    const pvsSaveResponse = await apolloClient.mutate({
      mutation: SAVE_PVS_CAMPAIGN_MUTATION,
      variables: { campaign: campaignModule },
      refetchQueries: refetchQueries(campaignModule.pvs_campaign_id)
    });

    console.log('pvsSaveResponse: ', pvsSaveResponse);
  } catch (error) {
    console.log('Failed to mutate pvs campaignModule');
    console.error(error);
    Promise.reject(false);
  }

  Promise.resolve(true);
}

/**
 * Will execute a gql mutation to publish the given PvsCampaign
 * @param apolloClient ApolloClient<any> uses to execute a mutation
 * @param campaign PvsCampaign object to be published
 */
async function publishCampaign(apolloClient: ApolloClient<any>, campaign: PvsCampaign) {
  try {
    // get values required to publish
    const publishInput = {
      tgrams_id: campaign.tgrams_id,
      campaign_id: campaign.pvs_campaign_id
    };

    console.log('pvsCampaign publishInput: ', publishInput);

    const pvsSaveResponse = await apolloClient.mutate({
      mutation: PUBLISH_PVS_CAMPAIGN_MUTATION,
      variables: { pvsInput: publishInput },
      refetchQueries: refetchQueries(campaign.pvs_campaign_id)
    });

    console.log('pvsPublishResponse: ', pvsSaveResponse);
  } catch (error) {
    console.log('Failed to publish pvs campaign');
    console.error(error);
    Promise.reject(false);
  }

  Promise.resolve(true);
}

/**
 * Will retrieve a PvsCampaign and render a component that enables the user to
 * edit the campaign configuration.
 * @param param0 CampaignModuleProps object
 */
function renderCampaignEdit({
  campaign,
  tgramsId,
  companyName,
  startDate,
  endDate,
  onModuleProductNameChange
}: CampaignModuleProps) {
  return (
    <PvsVerticalListContainer>
      {({ verticals }) => {
        return (
          <PvsCampaignContainer pvs_campaign_id={campaign.campaign_id}>
            {({ pvs, updateCampaignModule }) => {
              return (
                <PvsCampaignEdit
                  // from parent
                  tgramsId={tgramsId}
                  companyName={companyName}
                  campaignStartDate={startDate}
                  campaignEndDate={endDate}
                  campaignId={campaign.campaign_id}
                  onCampaignChange={updateCampaignModule}
                  onVerticalIdChange={onModuleProductNameChange}
                  // from child campaign container
                  adServerCampaignId={pvs.ad_server_campaign_id}
                  weight={pvs.weight}
                  lastSyncDate={pvs.last_sync_date}
                  isDirty={pvs.is_dirty}
                  name={pvs.ad_server_name}
                  isActive={pvs.is_active}
                  verticalId={pvs.vertical_id}
                  // other containers
                  verticals={verticals}
                />
              );
            }}
          </PvsCampaignContainer>
        );
      }}
    </PvsVerticalListContainer>
  );
}

/**
 * Will render a React component that displays the PvsCampaign module's Publish status and
 * provide the user with a button to trigger a publish.
 * @param campaign PvsCampaign to display publish status for
 * @param thomasCampaignStatus The status of the ThomasCampaign this PvsCampaign is associated with
 * @param productFulfillmentStatus The status of the ProductFufillment object this campaign is associated with
 * @param publishCampaign A function that publishes the PvsCampaign to the ad server
 */
function renderCampaignPublish(
  thomasCampaignStatus: number,
  productFulfillment: any,
  publishCampaign: () => any
) {
  return (
    <Query query={GET_CLIENT_PVS_CAMPAIGN_MODULE}>
      {({ data: { campaignModule } = { campaignModule : {} as any} }) => {
        if (!campaignModule) {
          return null;
        }

        if (!campaignModule.pvs_campaign_id) {
          return (
            <PvsCampaignPublish
              publishCampaign={publishCampaign}
              thomasCampaignStatus={thomasCampaignStatus}
              productFulfillment={productFulfillment}
              creatives={[]}
              campaign={campaignModule}
            />
          );
        }

        return (
          <PvsCreativeContainer pvs_campaign_id={campaignModule.pvs_campaign_id}>
            {({ creatives }) => (
              <PvsCampaignPublish
                publishCampaign={publishCampaign}
                thomasCampaignStatus={thomasCampaignStatus}
                productFulfillment={productFulfillment}
                creatives={creatives}
                campaign={campaignModule}
              />
            )}
          </PvsCreativeContainer>
        );
      }}
    </Query>
  );
}

export default {
  renderCampaignEdit,
  renderCampaignPublish,
  getCampaign,
  duplicateCampaign,
  saveCampaign,
  publishCampaign
};
